// Cookie consent (CMP). Bundled as its own esbuild entry point, loaded from <head> rather than through
// site.ts, so the banner and the Consent Mode gate run as early as possible - before site.js at the end
// of <body>. Reads its configuration from a JSON script block the server renders (see
// Views/Partials/Integrations/_CookieConsent.cshtml) rather than from generated code, so this file stays
// data-driven and type-checked.

import { isbot } from "isbot";
import { run, acceptedCategory, type CookieConsentConfig, type Section } from "vanilla-cookieconsent";

interface CookieConsentPageConfig {
    gtmContainerId: string;
    consentRevision: number;
    privacyPolicyUrl: string | null;
    // Null when the Site Settings info email is not filled in.
    infoEmail: string | null;
    // Null when anonymous page views are off or the page is an error page.
    pageViewToken: string | null;
}

// One analytics cookie inventory: it renders the disclosure table and, for the entries this site can
// reach, the autoClear list. Keeping both from one source stops the table and the cleanup drifting.
interface AnalyticsCookie {
    name: string;
    domain: string;
    desc: string;
    exp: string;
    // Set only for cookies written on this site's own host. One on clarity.ms or bing.com is disclosed
    // but cannot be removed from here.
    clearPattern?: RegExp;
}

// Declared locally rather than in globals.d.ts: esbuild bundles this file as an IIFE, so this gtag
// stays private to the bundle and does not collide with the Consent Mode default stub declared inline
// in <head>. Both push onto the same window.dataLayer array.
declare global {
    interface Window {
        dataLayer: unknown[];
        clarity?: ClarityCommand;
    }

    // Not yet in the TypeScript DOM library. Undefined in browsers without prerendering.
    interface Document {
        prerendering?: boolean;
    }
}

window.dataLayer = window.dataLayer || [];

// The CMP's own cookie. Declared once, so the library configuration and the "Strictly necessary" row
// that discloses it cannot drift apart.
const consentCookieName: string = "cc_cookie";
const consentCookieExpiryDays: number = 365;

const analyticsCookies: AnalyticsCookie[] = [
    { name: "_ga", domain: "Google Analytics", desc: "Distinguishes visitors", exp: "2 years", clearPattern: /^_ga/ },
    // The library writes table cells as HTML, so the angle brackets are escaped.
    { name: "_ga_&lt;measurement-id&gt;", domain: "Google Analytics", desc: "GA4 session state", exp: "2 years" },
    { name: "_clck", domain: "Microsoft Clarity", desc: "Clarity user id and preferences", exp: "1 year", clearPattern: /^_clck$/ },
    { name: "_clsk", domain: "Microsoft Clarity", desc: "Links page views within one session", exp: "1 day", clearPattern: /^_clsk$/ },
    { name: "CLID", domain: "clarity.ms", desc: "Identifies first Clarity sighting of the user", exp: "1 year" },
    { name: "ANONCHK", domain: "clarity.ms", desc: "Ads-flag indicator", exp: "10 minutes" },
    { name: "MR", domain: "bing.com / clarity.ms", desc: "Whether to refresh MUID", exp: "7 days" },
    { name: "MUID", domain: "bing.com", desc: "Microsoft user id across Microsoft domains", exp: "1 year" },
    { name: "SM", domain: "clarity.ms", desc: "Synchronises MUID across Microsoft domains", exp: "Session" },
];

// Universal Analytics cookies, which GA4 does not set. Cleared but not disclosed: a visitor can only
// still carry one from before the GA4 migration, so the table would list cookies this site never writes.
const legacyAnalyticsCookiePatterns: RegExp[] = [/^_gid$/, /^_gat/];

type GtagCommand = (command: string, action: string, parameters: Record<string, string>) => void;

// Clarity's command queue. `q` holds the calls made before clarity.js loads; Clarity drains it on load.
type ClarityCommand = { (command: string, options?: Record<string, string>): void; q?: unknown[] };

const anonymousPageViewPath: string = "/api/anonymous-page-view";

let gtmInjected: boolean = false;
let anonymousPageViewSent: boolean = false;

document.addEventListener("DOMContentLoaded", init);

// Consent Mode accepts a dataLayer entry only when its class is Arguments. A rest parameter gives a
// plain Array, which Consent Mode drops without an error, so the implementation takes no parameters
// and pushes its own `arguments`. The annotation still checks the call sites: a function with fewer
// parameters satisfies a type with more.
const gtag: GtagCommand = function (): void {
    window.dataLayer.push(arguments);
};

function injectGtm(gtmContainerId: string): void {
    if (gtmInjected) {
        return;
    }

    gtmInjected = true;

    const script: HTMLScriptElement = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtm.js?id=" + gtmContainerId;

    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    const firstScript: HTMLScriptElement | undefined = document.getElementsByTagName("script")[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
}

// Both Clarity projects carry "track":false in their tag configuration - the project's own Cookie
// consent setting - so Clarity records but writes no cookies until the page calls clarity("consentv2").
// GTM injects the Clarity tag afterwards, so this declares the same queue stub Clarity's own snippet
// uses and queues the call. Clarity keeps an existing window.clarity and moves its own "start" command
// to the head of the queue, so the queued consent is applied to a started session.
function setClarityConsent(state: "granted" | "denied"): void {
    if (!window.clarity) {
        // Nothing to withdraw when Clarity never loaded.
        if (state === "denied") {
            return;
        }

        const queue: ClarityCommand = function (): void {
            (queue.q = queue.q || []).push(arguments);
        };

        window.clarity = queue;
    }

    window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: state });
}

// Consent alone selects the branch; the container id only decides whether GTM loads.
function applyConsent(gtmContainerId: string): void {
    if (acceptedCategory("analytics")) {
        gtag("consent", "update", { analytics_storage: "granted" });
        setClarityConsent("granted");

        if (gtmContainerId !== "") {
            injectGtm(gtmContainerId);
        }

        return;
    }

    // analytics consent is denied
    gtag("consent", "update", { analytics_storage: "denied" });
    setClarityConsent("denied");
}

// The token names the page, so the beacon body is the token alone. sendBeacon sends it as text/plain with
// an Origin header and survives the page being closed.
function sendAnonymousPageView(token: string | null): void {
    // GA4 does no bot filtering on Measurement Protocol events, so it is done here. Only a bot that runs
    // JavaScript gets this far, and the user agent check catches the ones that identify themselves. The
    // server does not repeat it: a request sent without this script can carry any user agent.
    // Puppeteer, Playwright and Selenium set navigator.webdriver. A user agent check cannot see them.
    if (anonymousPageViewSent || token === null || navigator.webdriver || isbot(navigator.userAgent)) {
        return;
    }

    // A prerendered page is not a view until the visitor opens it. If the visitor never opens it, nothing is sent.
    if (document.prerendering) {
        document.addEventListener("prerenderingchange", (): void => sendAnonymousPageView(token), { once: true });
        return;
    }

    anonymousPageViewSent = true;
    navigator.sendBeacon(anonymousPageViewPath, token);
}

function readConfig(): CookieConsentPageConfig | null {
    const element: HTMLElement | null = document.getElementById("cookie-consent-config");

    if (!element || !element.textContent) {
        return null;
    }

    try {
        return JSON.parse(element.textContent) as CookieConsentPageConfig;
    } catch {
        return null;
    }
}

// 30.44 is the average month length, so the 365-day lifetime reads as "12 months".
function formatExpiryInMonths(days: number): string {
    const months: number = Math.round(days / 30.44);

    return months === 1 ? "1 month" : `${months} months`;
}

// No `domain` on the entries. Given one, the library erases only with `.<domain>`; given none, it makes
// two attempts - one with no Domain attribute, which is the only way to erase a host-only cookie, and one
// with `.<hostname>`. The fallback hostname is the same value this used to pass in.
function buildAutoClearCookies(): { name: RegExp }[] {
    const patterns: RegExp[] = analyticsCookies
        .flatMap((cookie: AnalyticsCookie) => (cookie.clearPattern ? [cookie.clearPattern] : []))
        .concat(legacyAnalyticsCookiePatterns);

    return patterns.map((pattern: RegExp) => ({ name: pattern }));
}

function buildAnalyticsCookieTable(): Record<string, string>[] {
    return analyticsCookies.map((cookie: AnalyticsCookie) => ({
        name: cookie.name,
        domain: cookie.domain,
        desc: cookie.desc,
        exp: cookie.exp,
    }));
}

// Only the first two rows are this site's own cookies; the rest come from the edge and hosting
// platforms, which the application knows nothing about, so they stay written out.
function buildNecessaryCookieTable(siteDomain: string): Record<string, string>[] {
    return [
        { name: consentCookieName, domain: siteDomain, desc: "Stores your cookie choices, consent id and policy revision", exp: formatExpiryInMonths(consentCookieExpiryDays) },
        { name: ".AspNetCore.Antiforgery.*", domain: siteDomain, desc: "CSRF protection on form pages", exp: "Session" },
        { name: "__cf_bm", domain: "Cloudflare", desc: "Bot management", exp: "30 minutes" },
        { name: "_cfuvid", domain: "Cloudflare", desc: "Rate-limiting and abuse protection", exp: "Session" },
        { name: "cf_clearance", domain: "Cloudflare", desc: "Records a passed security challenge", exp: "30 days" },
        { name: "ARRAffinity, ARRAffinitySameSite", domain: "Umbraco Cloud", desc: "Load-balancer session affinity", exp: "Session" },
    ];
}

// Returns no section when no info email is configured: the section holds nothing but the address, so
// an empty mailto: link would be its whole content.
function buildMoreInformationSections(infoEmail: string | null, privacyPolicyUrl: string): Section[] {
    const email: string = infoEmail ? infoEmail.trim() : "";

    if (email === "") {
        return [];
    }

    return [
        {
            title: "More information",
            description:
                `Read our <a href="${privacyPolicyUrl}">Privacy Policy</a> to learn how we use cookies and your data. ` +
                `Questions? Email us at <a href="mailto:${email}">${email}</a>.`,
        },
    ];
}

async function init(): Promise<void> {
    const config: CookieConsentPageConfig | null = readConfig();

    if (!config) {
        return;
    }

    // The host the site is served from, not a fixed production name: the same disclosure has to be
    // accurate on local, staging and production.
    const siteDomain: string = window.location.hostname;

    // Falls back to the plink path while the Site Settings property is empty, so the banner always
    // offers the policy link.
    const privacyPolicyUrl: string = config.privacyPolicyUrl || "/plink/legal/privacy-policy/";

    const consentConfig: CookieConsentConfig = {
        revision: config.consentRevision,
        cookie: {
            name: consentCookieName,
            expiresAfterDays: consentCookieExpiryDays,
            sameSite: "Lax",
            secure: true,
        },
        guiOptions: {
            consentModal: {
                layout: "box",
                position: "bottom right",
                equalWeightButtons: true,
                flipButtons: false,
            },
            preferencesModal: {
                layout: "box",
                equalWeightButtons: true,
                flipButtons: false,
            },
        },
        categories: {
            necessary: {
                enabled: true,
                readOnly: true,
            },
            analytics: {
                autoClear: {
                    cookies: buildAutoClearCookies(),
                    reloadPage: true,
                },
            },
        },
        // onConsent covers both the first consent action and every later page load, so no
        // onFirstConsent handler: it would only repeat the same work on the first action.
        onConsent: (): void => applyConsent(config.gtmContainerId),
        onChange: (): void => applyConsent(config.gtmContainerId),
        language: {
            default: "en",
            translations: {
                en: {
                    consentModal: {
                        title: "Cookies on this site",
                        description:
                            "Some cookies are needed to make the site work. We always use those. " +
                            "With your consent we also use Google Analytics and Microsoft Clarity. They show us how " +
                            "people use the site: which pages they visit, and how they move around a page. Clarity " +
                            "records this as a replay, but anything you type stays hidden. " +
                            "You can change your choice at any time under “Cookie settings” in the footer." +
                            `<br><a href="${privacyPolicyUrl}">Privacy Policy</a>`,
                        acceptAllBtn: "Accept all",
                        acceptNecessaryBtn: "Reject all",
                        showPreferencesBtn: "Manage preferences",
                        closeIconLabel: "Close",
                        footer: config.privacyPolicyUrl
                            ? `<a href="${config.privacyPolicyUrl}">Privacy Policy</a>`
                            : undefined,
                    },
                    preferencesModal: {
                        title: "Cookie preferences",
                        acceptAllBtn: "Accept all",
                        acceptNecessaryBtn: "Reject all",
                        savePreferencesBtn: "Save preferences",
                        closeIconLabel: "Close",
                        sections: [
                            {
                                title: "Strictly necessary",
                                description:
                                    "Required for the site to function, to protect it from abuse, and to remember " +
                                    "your cookie choice. Cannot be switched off.",
                                linkedCategory: "necessary",
                                cookieTable: {
                                    headers: { name: "Cookie", domain: "Domain", desc: "Purpose", exp: "Expires" },
                                    body: buildNecessaryCookieTable(siteDomain),
                                },
                            },
                            {
                                title: "Analytics",
                                description:
                                    "Google Analytics 4 (usage statistics) and Microsoft Clarity (session replays and " +
                                    "heatmaps, with form fields masked). Processed by Google and Microsoft; see the " +
                                    "Privacy Policy for transfers outside the EU.",
                                linkedCategory: "analytics",
                                cookieTable: {
                                    headers: { name: "Cookie", domain: "Domain", desc: "Purpose", exp: "Expires" },
                                    body: buildAnalyticsCookieTable(),
                                },
                            },
                            ...buildMoreInformationSections(config.infoEmail, privacyPolicyUrl),
                        ],
                    },
                },
            },
        },
    };

    await run(consentConfig);

    // Counts both a visitor who declined and one who has not answered. This must come after run(): before
    // it the library has not read the consent cookie, so an accepted visitor would look not accepted.
    // A visitor who accepts on this page is counted twice for it (here, then by GA4 after GTM loads),
    // because the library does not reload the page on accept. That error is known and accepted. After
    // Accept -> Reject in Cookie settings the page reloads, and the second count is correct.
    if (!acceptedCategory("analytics")) {
        sendAnonymousPageView(config.pageViewToken);
    }
}
